import { Component, EventEmitter, Input, Output, Signal, effect, inject } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { Role } from '@core/enums/roles';
import { ILoginResponse } from '@core/interfaces/ilogin-response';
import { INavbarItems } from '@core/interfaces/inavbar-items';
import { Router } from '@angular/router';
import { Navbar } from '@core/enums/navbar';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'prism-navbar',
  standalone: false,
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss'
})
export class NavbarComponent {
  private router = inject(Router);
  private authService = inject(AuthService);

  @Input() openSideMenu: boolean = false;
  @Input() isFooter: boolean = false;
  @Output() openLoginModal: EventEmitter<any> = new EventEmitter();
  @Output() toggleSideMenu: EventEmitter<any> = new EventEmitter();
  @Output() closeSideMenu: EventEmitter<void> = new EventEmitter();

  currentUser: Signal<ILoginResponse | null> = this.authService.currentUserInfo;
  navbarItems: INavbarItems[] = [];

  get isLoggedIn(): boolean {
    return !!this.currentUser() && this.authService.checkLogin();
  }

  Navbar = Navbar;
  role = Role;
  logoUrl = 'assets/images/prism-logo-secondary-rgb.png';
  iconImgUrl: string = 'assets/images/Icon feather-user.svg';
  userFullName: string = '';
  accountSettingsLink: string = '/account-settings';
  isSiteOffline = environment.isSiteOffline || false;

  constructor() {
    effect(() => {
      this.setMenuItems();
      this.iconImgUrl = this.isLoggedIn && this.currentUser() ?
        'assets/images/Icon feather-user.svg' :
        'assets/images/login icon.svg';
      this.userFullName = this.currentUser()?.fullName ?? '';
    });
  }

  setMenuItems(): void {
    if (this.isLoggedIn) {
      if (this.currentUser()?.roleId === Role.User) {
        this.navbarItems = [
          { label: 'Home', link: '/dashboard' },
          { label: 'About', link: '/about' },
          { label: 'FAQ', link: '/faq' },
          { label: 'Courses', link: '/courses' },
          {
            label: 'Resources', link: '/resources',
            childItems:
              [
                { label: 'Resources', link: 'resources' },
                { label: 'Facilitation Guide', link: 'resources/facilitation-guide' },
              ],
            open: false
          }
        ];
      } else if (this.currentUser()?.roleId === Role.DistrictAdmin || this.currentUser()?.roleId === Role.SystemAdmin) {
        this.navbarItems = [
          { label: 'Home', link: '/dashboard' },
          { label: 'About', link: '/about' },
          { label: 'FAQ', link: '/faq' },
          { label: 'Courses', link: '/courses' },
          {
            label: 'Resources',
            link: '/resources',
            childItems:
              [
                { label: 'Resources', link: 'resources' },
                { label: 'Facilitation Guide', link: 'resources/facilitation-guide' },
              ],
            open: false
          },
          {
            label: 'Management',
            link: '/user-management',
            childItems:
              [
                { label: 'User Management', link: '/user-management' },
                { label: 'District Management', link: '/district-management' },
                { label: 'School Management', link: '/school-management' },
              ],
            open: false
          },
          { label: 'Reports', link: '/report' }
        ];
      }
    } else {
      this.navbarItems = [
        { label: 'Home', link: '/home' },
        { label: 'About', link: '/about' },
        { label: 'FAQ', link: '/faq' },
        {
          label: 'Resources',
          link: '/resources'
        },
      ];
      if (this.isFooter) this.navbarItems.push({ label: 'Contact Us', link: '/contact-us' });
    }
  }

  onLoginClick(): void {
    this.openLoginModal.emit();
  }

  isActive(link: string): boolean {
    return this.router.url === link;
  }

  trackByIndex(index: number): number {
    return index;
  }

  getSubmenuId(label: string, isFooter: boolean = false): string {
    const prefix = isFooter ? 'footer-submenu-' : 'submenu-';
    return prefix + label.toLowerCase().replace(/\s+/g, '-');
  }

  toggleDropdown(menuItem: INavbarItems): void {
    const wasOpen = menuItem.open;
    this.navbarItems.forEach(i => i.open = false);
    menuItem.open = !wasOpen;
  }

  openDropdown(menuItem: INavbarItems, event: Event): void {
    event.preventDefault();
    this.navbarItems.forEach(i => i.open = false);
    menuItem.open = true;
    setTimeout(() => {
      const submenu = document.getElementById(this.getSubmenuId(menuItem.label, this.isFooter));
      submenu?.querySelector<HTMLElement>('a, button')?.focus();
    });
  }

  closeDropdown(menuItem: INavbarItems, event?: Event): void {
    if (!menuItem.childItems || !menuItem.open) return;
    event?.preventDefault();
    menuItem.open = false;
    const submenuId = this.getSubmenuId(menuItem.label, this.isFooter);
    document.querySelector<HTMLElement>(`[aria-controls="${submenuId}"]`)?.focus();
  }

  onChildItemClick(parentItem: INavbarItems, _childItem: INavbarItems): void {
    parentItem.open = false;
    this.closeSideMenu.emit();
  }

  onMenuFocusOut(event: FocusEvent, menuItem: INavbarItems): void {
    if (!menuItem.open) return;
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement | null;
    if (!relatedTarget || !currentTarget || !currentTarget.contains(relatedTarget)) {
      menuItem.open = false;
    }
  }

  handleSideMenuToggle(): void {
    this.toggleSideMenu.emit();
  }

  logout(): void {
    this.authService.logout();
  }
}
